
import React from 'react';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';
import { View, Search, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import { useOptimizedWarehouseProcurements } from '@/hooks/use-optimized-warehouse-procurements';

const OptimizedWarehouseConfirmedList: React.FC = () => {
  const {
    procurements,
    isLoading,
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    setSearchTerm,
    goToPage,
    hasNextPage,
    hasPreviousPage
  } = useOptimizedWarehouseProcurements({ status: 'confirmed' });

  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1">
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
        <div className="text-sm text-muted-foreground mb-4">
          <Skeleton className="h-4 w-48" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by product name or notes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Results info */}
      <div className="text-sm text-muted-foreground mb-4">
        Showing {procurements.length} of {totalItems} confirmed procurements 
        {totalPages > 1 && ` (Page ${currentPage} of ${totalPages})`}
      </div>

      {procurements.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            {searchTerm 
              ? "No confirmed procurements found matching your search." 
              : "No procurements have been confirmed by warehouse yet."}
          </p>
        </div>
      ) : (
        <>
          {/* Procurement List */}
          <div className="space-y-4">
            {procurements.map((procurement) => (
              <Card key={procurement.id}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{procurement.product_name || 'N/A'}</h3>
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Confirmed
                        </Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                        <div>
                          <span className="font-medium">Quantity:</span> {procurement.total_quantity || 0} kg
                        </div>
                        <div>
                          <span className="font-medium">Amount:</span> Br{procurement.total_amount || 0}
                        </div>
                        {procurement.warehouse?.net_kg_payable && (
                          <div>
                            <span className="font-medium">Net Payable:</span> {procurement.warehouse.net_kg_payable} kg
                          </div>
                        )}
                        <div>
                          <span className="font-medium">Confirmed:</span> {
                            procurement.warehouse?.warehouse_confirmation_date 
                              ? format(new Date(procurement.warehouse.warehouse_confirmation_date), 'PP p')
                              : 'N/A'
                          }
                        </div>
                      </div>
                      {procurement.warehouse?.inspection_notes && (
                        <div className="text-sm">
                          <span className="font-medium">Inspection Notes:</span> {procurement.warehouse.inspection_notes}
                        </div>
                      )}
                    </div>
                    <div className="ml-4 flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/procurements/${procurement.id}`)}
                      >
                        <View className="mr-1 h-4 w-4" />
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={!hasPreviousPage}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={!hasNextPage}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default OptimizedWarehouseConfirmedList;
